import React, { useEffect, useState } from "react";
import { Sidebar, Menu, MenuItem, sidebarClasses } from "react-pro-sidebar";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Typography, useTheme, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import FolderIcon from "@mui/icons-material/Folder";
import GroupsIcon from "@mui/icons-material/Groups";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import FunctionsIcon from "@mui/icons-material/Functions";
import SecurityIcon from "@mui/icons-material/Security";
import DoctorService from "../../services/doctorService";
import SidebarUserProfile from "../../components/SidebarUserProfile";
import SidebarMenuItem from "../../components/SidebarMenuItem";
import SidebarSection from "../../components/SidebarSection";

export default function Sidebaar({ isCollapsed, setIsCollapsed }) {
  const theme = useTheme(); //useTheme hook'u, ThemeProvider tarafından sağlanan temayı almanıza yardımcı olur.
  const colors = tokens(theme.palette.mode); //Başlangıç olarak dark
  const { t } = useTranslation('sidebar');
  const [selected, setSelected] = useState('dashboard'); // Use key instead of translated value
  const navigate = useNavigate();
  const [doctorInfo, setDoctorInfo] = useState(null);
  
  const doctorService = new DoctorService();

  // Component yüklendiğinde doktor bilgilerini çek
  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const info = await doctorService.getDoctorInfo();
        const normalizedDoctor = info?.doctor || info || {};
        const normalizedPermissions =
          info?.permissions ||
          info?.doctor?.permissions ||
          info?.permission ||
          {};
        setDoctorInfo({ doctor: normalizedDoctor, permissions: normalizedPermissions });
        console.log("Sidebar Doctor Info:", info);
        console.log("Doctor Role:", normalizedDoctor?.role);
      } catch (error) {
        console.error("Error fetching doctor info:", error);
      }
    };
    fetchDoctorInfo();
  }, []);

  const doctorRole = doctorInfo?.doctor?.role || doctorInfo?.role;
  // API farklı yapılar dökebiliyor; olası alanların hepsini topla
  const doctorPermissions =
    doctorInfo?.permissions ||
    doctorInfo?.doctor?.permissions ||
    doctorInfo?.permission ||
    {};
  const canViewPermissionGroups =
    doctorRole === "admin" ||
    doctorPermissions?.can_own_permission_groups_view === true ||
    doctorPermissions?.can_permission_groups_view === true;
  return (
    <Box 
      sx={{
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Sidebar
        rootStyles={{
          [`.${sidebarClasses.container}`]: {
            backgroundColor: colors.primary[400],
            backgroundImage: `linear-gradient(180deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            height: "100vh",
            borderRight: `1px solid ${colors.grey[800]}30`,
            boxShadow: `4px 0 24px rgba(0, 0, 0, 0.15)`,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        collapsed={isCollapsed}
        width="280px"
        collapsedWidth="80px"
      >
        <Box 
          sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
              borderRadius: '10px',
              margin: '8px 0',
            },
            '&::-webkit-scrollbar-thumb': {
              background: `linear-gradient(180deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
              borderRadius: '10px',
              border: `2px solid ${colors.primary[400]}`,
              transition: 'all 0.3s ease',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: `linear-gradient(180deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
              boxShadow: `0 0 8px ${colors.blueAccent[500]}60`,
            },
          }}
        >
          <Menu
            menuItemStyles={{
              button: {
                '&:hover': {
                  backgroundColor: `${colors.blueAccent[800]}20`,
                  transform: 'translateX(4px)',
                },
                transition: 'all 0.3s ease',
              },
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
          {/* Header with Logo and Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'space-between',
              p: isCollapsed ? 2 : 3,
              mb: 2,
              borderBottom: `1px solid ${colors.grey[800]}30`,
              background: `linear-gradient(135deg, ${colors.blueAccent[800]}15 0%, transparent 100%)`,
            }}
          >
            {!isCollapsed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
                  }}
                >
                  <DashboardCustomizeIcon sx={{ color: 'white', fontSize: '22px' }} />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.5px'
                  }}
                >
                  {t('appName')}
                </Typography>
              </Box>
            )}
            
            <Tooltip title={isCollapsed ? t('expand') : t('collapse')} placement="right">
              <IconButton
                onClick={() => setIsCollapsed(!isCollapsed)}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: `${colors.blueAccent[700]}30`,
                  border: `1px solid ${colors.blueAccent[500]}40`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: `${colors.blueAccent[600]}50`,
                    transform: 'rotate(180deg)',
                  }
                }}
              >
                {isCollapsed ? (
                  <ChevronRightIcon sx={{ color: colors.blueAccent[300] }} />
                ) : (
                  <ChevronLeftIcon sx={{ color: colors.blueAccent[300] }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          {/* User Profile Section */}
          <SidebarUserProfile doctorInfo={doctorInfo} isCollapsed={isCollapsed} />

          {/* Spacer for collapsed mode - pushes menu items to bottom */}
          {isCollapsed && (
            <Box 
              sx={{ 
                flex: 1,
                minHeight: '200px',
              }} 
            />
          )}

          {/* Main Navigation */}
          <SidebarSection 
            title={t('main')} 
            icon={<HomeOutlinedIcon />}
            isCollapsed={isCollapsed}
          >
            <SidebarMenuItem
              title={t('dashboard')}
              itemKey="dashboard"
              icon={<HomeOutlinedIcon />}
              onClick={() => navigate("/dashboard")}
              selected={selected}
              setSelected={setSelected}
            />
          </SidebarSection>

          {/* Data Management Section */}
          <SidebarSection 
            title={t('management')} 
            icon={<GroupsIcon />}
            isCollapsed={isCollapsed}
          >
            <SidebarMenuItem
              title={t('manageTeam')}
              itemKey="manageTeam"
              icon={<PeopleOutlinedIcon />}
              onClick={() => navigate("/team")}
              selected={selected}
              setSelected={setSelected}
            />
            <SidebarMenuItem
              title={t('patients')}
              itemKey="patients"
              icon={<ContactsOutlinedIcon />}
              onClick={() => navigate("/patients")}
              selected={selected}
              setSelected={setSelected}
            />
            <SidebarMenuItem
              title={t('files')}
              itemKey="files"
              icon={<FolderIcon />}
              onClick={() => navigate("/files")}
              selected={selected}
              setSelected={setSelected}
            />
            {canViewPermissionGroups && (
              <SidebarMenuItem
                title={t('permissionGroups')}
                itemKey="permissionGroups"
                icon={<SecurityIcon />}
                onClick={() => navigate("/doctor-permission-groups")}
                selected={selected}
                setSelected={setSelected}
              />
            )}
          </SidebarSection>

          {/* Actions Section */}
          <SidebarSection 
            title={t('actions')} 
            icon={<PersonAddIcon />}
            isCollapsed={isCollapsed}
          >
            <SidebarMenuItem
              title={t('inviteDoctor')}
              itemKey="inviteDoctor"
              icon={<EmailIcon />}
              onClick={() => navigate("/inviteDoctor")}
              selected={selected}
              setSelected={setSelected}
              isNew={true}
            />
            <SidebarMenuItem
              title={t('addPatient')}
              itemKey="addPatient"
              icon={<PersonAddIcon />}
              onClick={() => navigate("/AddPatient")}
              selected={selected}
              setSelected={setSelected}
            />
          </SidebarSection>

          {/* Tools Section */}
          <SidebarSection 
            title={t('tools')} 
            icon={<CalendarTodayOutlinedIcon />}
            isCollapsed={isCollapsed}
          >
            <SidebarMenuItem
              title={t('scoringFormulas')}
              itemKey="scoringFormulas"
              icon={<FunctionsIcon />}
              onClick={() => navigate("/formulas")}
              selected={selected}
              setSelected={setSelected}
              isNew={true}
            />
            <SidebarMenuItem
              title={t('calendar')}
              itemKey="calendar"
              icon={<CalendarTodayOutlinedIcon />}
              onClick={() => navigate("/calendar")}
              selected={selected}
              setSelected={setSelected}
            />
            <SidebarMenuItem
              title={t('faq')}
              itemKey="faq"
              icon={<HelpOutlineOutlinedIcon />}
              onClick={() => navigate("/faq")}
              selected={selected}
              setSelected={setSelected}
            />
          </SidebarSection>

          </Menu>
        </Box>

        {/* Footer Info - Menu'nun dışında, Sidebar'ın içinde */}
        {!isCollapsed && (
          <Box
            sx={{
              p: 2,
              m: 2,
              mt: 'auto',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${colors.blueAccent[800]}30 0%, ${colors.greenAccent[800]}20 100%)`,
              border: `1px solid ${colors.blueAccent[700]}40`,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[400],
                fontSize: '0.7rem',
                display: 'block',
                textAlign: 'center',
              }}
            >
              {t('version')} 2.0.0
            </Typography>
          </Box>
        )}
      </Sidebar>
    </Box>
  );
}
